import React, { useMemo, useState } from "react";

// Hands a project to another member of the workspace.
//
// Two situations lead here and the copy adapts to both:
//   - the owner handing over voluntarily (they keep editor access)
//   - an admin rescuing a project whose owner has left the workspace, which is
//     otherwise frozen: rename / archive / delete / share are all owner-only.
const TransferOwnershipModal = ({
  isOpen,
  onClose,
  onConfirm,
  project,
  members = [],
  loading = false,
  error = "",
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");

  // Reset the picker whenever a different project is opened.
  const projectId = project?.id;
  const [seenProjectId, setSeenProjectId] = useState(projectId);
  if (projectId !== seenProjectId) {
    setSeenProjectId(projectId);
    setSelectedId(null);
    setSearch("");
  }

  const ownerIsGone = project?.owner_is_former_member === true;
  const ownerName = project?.owner?.name || project?.owner?.email || "its previous owner";

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members
      // The current owner can't be handed their own project.
      .filter((m) => m.user_id !== project?.owner?.user_id)
      .filter((m) =>
        !q ||
        (m.full_name || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q)
      );
  }, [members, search, project]);

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-sm flex items-center justify-center font-outfit">
      <div className="bg-white rounded-2xl shadow-lg w-[520px] px-6 py-5">
        <h2 className="text-[#18181B] text-[20px] font-semibold mb-1">Transfer ownership</h2>
        <p className="text-[#71717A] text-[14px] leading-relaxed mb-4">
          {ownerIsGone ? (
            <>
              <span className="font-medium text-[#3F3F46]">{ownerName}</span> is no longer in this
              workspace, so nobody can rename, archive, delete or share{" "}
              <span className="font-medium text-[#3F3F46]">{project.name}</span>. Choose who should
              take it over.
            </>
          ) : (
            <>
              Choose who should own{" "}
              <span className="font-medium text-[#3F3F46]">{project.name}</span>. You'll keep editor
              access, but only the new owner can rename, archive, delete or share it.
            </>
          )}
        </p>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search members..."
          className="w-full mb-2 px-3 py-2 border border-[#E4E4E7] rounded-md text-[14px] outline-none focus:border-[#C95EBE]"
        />

        <div className="max-h-[240px] overflow-y-auto border border-[#E4E4E7] rounded-md divide-y divide-[#F4F4F5]">
          {candidates.length === 0 ? (
            <p className="text-[14px] text-[#A1A1AA] px-3 py-6 text-center">
              No other members in this workspace to transfer to.
            </p>
          ) : (
            candidates.map((m) => (
              <label
                key={m.user_id}
                className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#FAFAFA]"
              >
                <input
                  type="radio"
                  name="new-owner"
                  checked={selectedId === m.user_id}
                  onChange={() => setSelectedId(m.user_id)}
                  className="w-[16px] h-[16px] cursor-pointer"
                  style={{ accentColor: "#C95EBE" }}
                />
                <span className="min-w-0">
                  <span className="block text-[14px] text-[#18181B] truncate">
                    {m.full_name || m.email}
                  </span>
                  <span className="block text-[12px] text-[#A1A1AA] truncate">{m.email}</span>
                </span>
              </label>
            ))
          )}
        </div>

        {error ? <p className="text-[13px] text-[#E21919] mt-3">{error}</p> : null}

        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2 rounded-md border border-[#E4E4E7] text-[#52525B] text-[14px] font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(selectedId)}
            disabled={loading || !selectedId}
            className="px-5 py-2 rounded-md bg-[#A04A97] text-white text-[14px] font-semibold hover:bg-[#8a3f83] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Transferring…" : "Transfer ownership"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferOwnershipModal;
